import { useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BookingSuccessModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  successMessage?: string;
  summaryMessage: string;
  tutorName: string;
  childName: string;
}

const COLORS = [
  'bg-blue-500',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-yellow-500',
];

// Generate stable particle specs
const PARTICLE_SPECS = Array.from({ length: 32 }).map((_, i) => {
  const angle = (i * 360) / 32 + (Math.random() * 15 - 7.5); // spread evenly around the circle with slight jitter
  const distance = 100 + Math.random() * 120; // travel distance
  const delay = Math.random() * 300; // staggered delay in ms
  const scale = 0.4 + Math.random() * 0.7;
  const rotate = Math.random() * 720 - 360; // rotation degree
  const color = COLORS[i % COLORS.length];
  const width = 6 + Math.round(Math.random() * 14); // ribbon width
  const height = 4 + Math.round(Math.random() * 8); // ribbon height
  const isCircle = Math.random() > 0.4;
  return { id: i, color, angle, distance, delay, scale, rotate, width, height, isCircle };
});

export function BookingSuccessModal({
  open,
  onClose,
  title = 'Class Booked Successfully!',
  successMessage = 'Your class booking has been confirmed and the tutor has been notified.',
  summaryMessage,
  tutorName,
  childName,
}: BookingSuccessModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity animate-in fade-in-0 duration-300"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Styles for hardware-accelerated animations */}
      <style>{`
        @keyframes ribbon-burst {
          0% {
            transform: translate(-50%, -50%) translate(0, 0) scale(0) rotate(0deg);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) translate(var(--tx), var(--ty)) scale(var(--scale)) rotate(var(--rotate));
            opacity: 0;
          }
        }
        
        @keyframes icon-pop {
          0% {
            transform: scale(0.6);
            opacity: 0;
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes modal-slide-up {
          0% {
            transform: scale(0.95) translateY(10px);
            opacity: 0;
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
        
        .ribbon-particle {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: ribbon-burst 2.4s cubic-bezier(0.1, 0.85, 0.25, 1) forwards;
          pointer-events: none;
        }
        
        .success-icon-animate {
          animation: icon-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .modal-animate {
          animation: modal-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      {/* Modal Container */}
      <div
        role="dialog"
        aria-modal="true"
        className="modal-animate relative z-10 flex w-full max-w-md flex-col rounded-2xl bg-white p-6 text-center shadow-2xl dark:bg-slate-800 dark:text-slate-100 border border-slate-100 dark:border-slate-700"
      >
        {/* Top Right Close Button */}
        <button
          type="button"
          aria-label="Close success dialog"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Confetti Ribbon Animation Source Center */}
        <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center">
          {/* Confetti Particles */}
          {PARTICLE_SPECS.map((spec) => {
            const rad = (spec.angle * Math.PI) / 180;
            const tx = `${Math.cos(rad) * spec.distance}px`;
            const ty = `${Math.sin(rad) * spec.distance}px`;

            return (
              <div
                key={spec.id}
                className={`ribbon-particle ${spec.color} ${spec.isCircle ? 'rounded-full' : 'rounded-sm'}`}
                style={
                  {
                    '--tx': tx,
                    '--ty': ty,
                    '--scale': spec.scale,
                    '--rotate': `${spec.rotate}deg`,
                    animationDelay: `${spec.delay}ms`,
                    width: `${spec.width}px`,
                    height: `${spec.height}px`,
                  } as React.CSSProperties
                }
              />
            );
          })}

          {/* Success Icon */}
          <div className="success-icon-animate flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-md shadow-emerald-100/35 dark:bg-emerald-950/40 dark:text-emerald-400 dark:shadow-none">
            <Check className="h-8 w-8 stroke-[3]" />
          </div>
        </div>

        {/* Title & Success Message */}
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight mb-2">
          {title}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 px-2 mb-6">
          {successMessage}
        </p>

        {/* Booking Card Details */}
        <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 mb-6 text-left dark:border-slate-700 dark:bg-slate-900/20">
          <div className="flex flex-col gap-3">
            {/* Tutor & Child Info Row */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5 dark:border-slate-800">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Tutor</p>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{tutorName}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Child</p>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{childName}</p>
              </div>
            </div>

            {/* Human Readable Schedule Details */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">Schedule Details</p>
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-medium">
                {summaryMessage}
              </p>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <Button
          type="button"
          onClick={onClose}
          className="w-full h-10 bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-700 rounded-xl font-semibold shadow-md transition-all cursor-pointer"
        >
          Great, thanks!
        </Button>
      </div>
    </div>
  );
}
